import type { IconName } from '@customerio/pluma-icons';

/**
 * Static messaging-channel catalog for the Outbox. A *scoped port* of the
 * messaging slice of the Ember `app/utils/action-info.ts` (1100+ lines,
 * taxonomy-coupled): per channel a display name, short name, icon, and the
 * `deliveryStates` / `deliveryActions` arrays that drive the two status-filter
 * groups. The rest of `actionInfo` (composer/editor metadata) is out of scope.
 * Delivery-state labels/colors/descriptions live in `./states`.
 */

export interface DeliveryChannelInfo {
  /** Channel value used as the `type` filter (`action_type` minus `_action`). */
  value: string;
  /** Human name shown in the channel dropdown. */
  name: string;
  /** Lower-case short name used in placeholders ("Search by <short> name…"). */
  shortName: string;
  icon: IconName;
  /** States for the "Delivery Status" group (order-significant, from Ember). */
  deliveryStates: readonly string[];
  /** States for the "User Action Status" group. */
  deliveryActions: readonly string[];
  /**
   * Measures a channel reports. Its presence gates the Metrics timeline panel
   * (omitted for channels like `live_notification` that report no metrics,
   * mirroring the optional field on Ember's `action-info`) and seeds the
   * timeline's candidate events alongside `deliveryStates` / `deliveryActions`.
   * Copied from the matching Ember `action-info` entry.
   */
  deliveryMetrics?: readonly string[];
}

/**
 * Messaging channels, in the Ember `MESSAGING_TYPES` order. `deliveryStates` /
 * `deliveryActions` are copied verbatim from the matching `action-info` entry.
 */
export const DELIVERY_CHANNELS = [
  {
    value: 'email',
    name: 'Email',
    shortName: 'email',
    icon: 'email',
    deliveryStates: [
      'attempted',
      'sent',
      'deferred',
      'failed',
      'bounced',
      'suppressed',
      'undeliverable',
      'delivered',
      'holdout',
    ],
    deliveryActions: ['opened', 'clicked', 'converted', 'unsubscribed', 'spammed', 'topic_unsubscribed'],
    deliveryMetrics: [
      'sent',
      'delivered',
      'opened',
      'clicked',
      'converted',
      'unsubscribed',
      'drafted',
      'topic_unsubscribed',
    ],
  },
  {
    value: 'push',
    name: 'Push notification',
    shortName: 'push',
    icon: 'mobile',
    deliveryStates: ['attempted', 'sent', 'failed', 'bounced', 'suppressed', 'undeliverable', 'holdout', 'delivered'],
    deliveryActions: ['opened', 'converted'],
    deliveryMetrics: ['opened', 'converted', 'drafted'],
  },
  {
    value: 'slack',
    name: 'Slack message',
    shortName: 'Slack message',
    icon: 'slack',
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout'],
    deliveryActions: ['clicked'],
    deliveryMetrics: ['drafted'],
  },
  {
    value: 'twilio',
    name: 'SMS',
    shortName: 'SMS',
    icon: 'sms',
    deliveryStates: ['attempted', 'sent', 'failed', 'bounced', 'undeliverable', 'delivered', 'holdout', 'suppressed'],
    deliveryActions: ['clicked', 'converted', 'replied'],
    deliveryMetrics: ['converted', 'drafted', 'replied'],
  },
  {
    value: 'sms',
    name: 'SMS',
    shortName: 'SMS',
    icon: 'sms',
    deliveryStates: ['attempted', 'sent', 'failed', 'bounced', 'undeliverable', 'delivered', 'holdout', 'suppressed'],
    deliveryActions: ['clicked', 'converted', 'replied'],
    deliveryMetrics: ['converted', 'drafted', 'replied'],
  },
  {
    value: 'urban_airship',
    name: 'Airship',
    shortName: 'UA push',
    icon: 'mobile',
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout'],
    deliveryActions: ['clicked', 'converted'],
    deliveryMetrics: ['converted', 'drafted'],
  },
  {
    value: 'webhook',
    name: 'Webhook',
    shortName: 'webhook',
    icon: 'webhook',
    deliveryStates: ['attempted', 'sent', 'failed', 'undeliverable', 'holdout'],
    deliveryActions: ['clicked', 'spammed'],
    deliveryMetrics: ['drafted'],
  },
  {
    value: 'in_app',
    name: 'In-app message',
    shortName: 'in-app',
    icon: 'in-app',
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout'],
    deliveryActions: ['opened', 'clicked', 'converted', 'viewed'],
    deliveryMetrics: ['drafted', 'opened', 'clicked', 'converted', 'viewed'],
  },
  {
    value: 'inbox',
    name: 'Inbox message',
    shortName: 'Inbox Message',
    icon: 'notifications',
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout'],
    deliveryActions: ['opened', 'clicked', 'converted'],
    deliveryMetrics: ['drafted', 'opened', 'clicked', 'converted'],
  },
  {
    value: 'line',
    name: 'LINE message',
    shortName: 'LINE',
    icon: 'brand/line',
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout', 'delivered'],
    deliveryActions: ['clicked', 'converted'],
    deliveryMetrics: ['converted', 'drafted'],
  },
  {
    value: 'whatsapp',
    name: 'WhatsApp',
    shortName: 'WhatsApp',
    icon: 'brand/whatsapp',
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout', 'delivered'],
    deliveryActions: ['clicked', 'opened', 'converted'],
    deliveryMetrics: ['converted', 'drafted'],
  },
  // Appended after MESSAGING_TYPES like the Ember deliveries dropdown: live
  // notifications are a delivery channel but not a campaign-builder action.
  {
    value: 'live_notification',
    name: 'Live notification',
    shortName: 'live notification',
    icon: 'mobile',
    deliveryStates: ['attempted', 'sent', 'failed', 'bounced', 'suppressed', 'undeliverable', 'holdout', 'delivered'],
    deliveryActions: ['opened', 'converted'],
  },
] as const satisfies readonly DeliveryChannelInfo[];

/** A known Outbox channel value (`email`, `push`, `sms`, …), derived from the catalog. */
export type DeliveryChannelValue = (typeof DELIVERY_CHANNELS)[number]['value'];

// Keyed by `string`, not `DeliveryChannelValue`: lookups come from the API's raw
// `action_type`, which may name a channel this catalog doesn't enumerate.
const CHANNELS_BY_VALUE = new Map<string, DeliveryChannelInfo>(
  DELIVERY_CHANNELS.map((channel) => [channel.value, channel]),
);

/** Look up a channel by its `action_type`-minus-`_action` value. */
export function getDeliveryChannel(value: string): DeliveryChannelInfo | undefined {
  return CHANNELS_BY_VALUE.get(value);
}

/** The channel value for a delivery's `action_type` (strips the trailing `_action`). */
export function channelValueOf(actionType: string): string {
  return actionType.replace(/_action$/, '');
}
